<template>
  <div v-if="routeProduct" class="route-product-status d-flex align-center justify-end">
    <span v-if="showForecastViolationIcon" class="mr-4">
      <icon-tooltip
        :color="forecastViolationColor"
        icon="mdi-weather-cloudy-alert"
        :tooltip="$t('forecast_violation_tooltip', {type: translatedForecastViolationType, timeToViolation: humanDurationToForecastViolation}) "/>
    </span>
    <span v-if="showForecastStagingViolationIcon" class="mr-4">
      <icon-tooltip
        :color="forecastViolationColor"
        icon="mdi-weather-cloudy-alert"
        :tooltip="$t('forecast_staging_violation_tooltip', {type: translatedForecastViolationType, timeToViolation: humanDurationToForecastViolation}) "/>
    </span>
    <div class="text-no-wrap mr-4" v-html="formattedTemperature"></div>
    <v-chip-group>
      <icon-tooltip v-if="showRouteHasNotStartedIcon" color="success" icon="mdi-timer-sand" :tooltip="$t('route_has_not_started')"/>
      <icon-tooltip v-if="showAwaitingDataIcon" color="success" icon="mdi-dots-horizontal" :tooltip="$t('awaiting_data')"/>
      <icon-tooltip v-if="showNoViolationsIcon" color="success" icon="mdi-check-circle" :tooltip="$t('no_violations')"/>
      <icon-tooltip v-if="showColdIcon" color="temperature-cold" icon="mdi-snowflake" :tooltip="$t('cold')"/>
      <icon-tooltip v-if="showHotIcon" color="temperature-hot" icon="mdi-fire-circle" :tooltip="$t('hot')"/>
      <icon-tooltip v-if="showExcessiveGapInDataIcon" color="warning" icon="mdi-clock-outline" :tooltip="$t('time_gap')"/>
      <icon-tooltip v-if="showMissingDataWarningIcon" color="warning" icon="mdi-cancel" :tooltip="$t('no_data')"/>
      <icon-tooltip v-if="showMissingDataIcon" color="error" icon="mdi-cancel" :tooltip="$t('no_data')"/>
    </v-chip-group>
  </div>
  <div v-else class="route-product-status d-flex align-center justify-end">
    <v-icon color="grey">mdi-minus</v-icon>
  </div>
</template>

<script>

import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import RouteComponentMixin from '@/components/route/RouteComponentMixin'
import TemperatureConverterUtil from '@/util/TemperatureConverterUtil'
import IconTooltip from '@/components/shared/IconTooltip'
import Moment from 'moment-timezone'
import DateTimeUtil from '@/util/DateTimeUtil'
import { get } from 'lodash'

/**
 * Helper component to show route product status.
 */
export default {
  name: 'RouteProductStatus',
  components: {
    IconTooltip
  },
  mixins: [CurrentUserMixin, RouteComponentMixin],
  props: {
    /**
     * @var RouteProductRecord
     */
    routeProduct: {
      type: Object
    },
    /**
     * @var RouteRecord
     * We have a few evaluations to be done that need the route status, and instead of implicitly requiring the
     * routeProduct to be expanded correctly with the route, I'm just passing in the route directly via a prop here
     */
    route: {
      type: Object
    },
    forecastViolation: {
      type: Object
    }
  },
  computed: {
    /**
     * Return a string representing the temperature formatted to 1
     * significant digit and the the scale (e.g. -12.9 °F). Returns an
     * empty string if there is no temperature set.
     *
     * @return string
     */
    formattedTemperature () {
      const format = this.customerContext.tempFormat() || this.routeProduct.temperature_scale
      const temp = this.routeProduct.getCurrentSimulatedTemperature(format)
      const formattedTemp = TemperatureConverterUtil.formatTemperature(temp)
      if (formattedTemp !== undefined) {
        return `${formattedTemp} &deg;${format}`
      }

      return ''
    },

    /**
     * Returns a string representing the last time the temperature was
     * updated.
     *
     * @return string
     */
    formattedLastUpdate () {
      return Moment(this.routeProduct.verofresh_temperature_last_update)
        .tz(this.customerContext.timezone())
        .format('MMM d, Y H:mm A z')
    },

    showRouteHasNotStartedIcon () {
      return !this.route.started
    },

    showNoViolationsIcon () {
      if (!this.route.started || !this.hasTemperatureData) {
        return false
      }
      // A no-data condition surfaces only as a warning-level violation during
      // the 30-40 min escalation window. Without this guard the route
      // would show the green "no violations" check while data is missing
      if (this.routeProduct.getViolations('no-data', ['warning', 'fatal']).length > 0) {
        return false
      }
      return this.filterTimeGapViolations(this.routeProduct.getViolations('*', 'fatal')).length === 0
    },

    showColdIcon () {
      return this.routeProduct.getViolations('cold', 'fatal').length > 0
    },

    showHotIcon () {
      return this.routeProduct.getViolations('hot', 'fatal').length > 0
    },

    showExcessiveGapInDataIcon () {
      return this.filterTimeGapViolations(this.routeProduct.getViolations(RouteViolationRecord.TYPE_GAP, 'fatal')).length > 0
    },

    showMissingDataIcon () {
      return this.routeProduct.getViolations('no-data', 'fatal').length > 0
    },

    showMissingDataWarningIcon () {
      return this.routeProduct.getViolations('no-data', 'warning').length > 0
    },

    showAwaitingDataIcon () {
      // Route has started but no temperature data has arrived yet, and the gap
      // hasn't escalated to a warning/fatal no-data violation (i.e. still within
      // the grace window).
      return this.route.started &&
        !this.hasTemperatureData &&
        this.routeProduct.getViolations('no-data', ['warning', 'fatal']).length === 0
    },

    /**
     * True when the route product has at least one simulated temperature
     * reading. Used to distinguish an in-spec route (green check) from one that
     * has no data yet. Mirrors RouteProductRecord.getCurrentSimulatedTemperature.
     */
    hasTemperatureData () {
      return typeof this.routeProduct.verofresh_temperature === 'number'
    },

    showForecastViolationIcon () {
      return this.forecastViolation != null && get(this.forecastViolation, 'level') === 'forecast'
    },

    showForecastStagingViolationIcon () {
      return this.forecastViolation != null && get(this.forecastViolation, 'level') === 'forecast_staging'
    },

    /**
     * Returns the color for the forecast violation icon based on violation type
     * @return string
     */
    forecastViolationColor () {
      const type = get(this.forecastViolation, 'type')
      if (type === 'cold') {
        return 'temperature-cold'
      } else if (type === 'hot') {
        return 'temperature-hot'
      }
      return 'warning'
    },

    /**
     * @return string
     */
    translatedForecastViolationType () {
      return this.$t(get(this.forecastViolation, 'type'))
    },

    /**
     * Returns a human friendly representation of the duration from now to the
     * time the forecasted violation will occur (e.g. "2 hours, 37 minutes")
     *
     * @return string
     */
    humanDurationToForecastViolation () {
      const violationStart = get(this.forecastViolation, 'start')
      const secondsToStart = Moment(violationStart).diff(Moment())
      const durationToStart = Moment.duration(secondsToStart)
      return DateTimeUtil.humanizeDurationPrecisely(durationToStart, { numberOfSignificantParts: 2 })
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
